import { useEffect, useMemo, useState } from 'react';
import { camelizeKeys } from '../../../utils/camelizeKeys';

const TAB = 'offshore_symbols';

const emptyPagination = {
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  perPage: 10,
};

const sortByOptions = [
  { value: 'name', label: 'nome' },
  { value: 'ticker', label: 'ticker' },
  { value: 'currency', label: 'moeda' },
  { value: 'exchange', label: 'exchange' },
  { value: 'country', label: 'país' },
  { value: 'strategy', label: 'estratégia' },
];

const useOffshoreSymbols = ({
  initialAssets = [],
  initialPagination = emptyPagination,
  initialBases = [],
  initialFilterOptions = {},
  search = '',
  enabled = true,
}) => {
  const [assets, setAssets] = useState(initialAssets);
  const [bases, setBases] = useState(initialBases);
  const [filterOptions, setFilterOptions] = useState(initialFilterOptions);
  const [pagination, setPagination] = useState(initialPagination);

  const [debouncedSearch, setDebouncedSearch] = useState(search);

  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const [editAssetId, setEditAssetId] = useState(null);
  const [showEditAssetModal, setShowEditAssetModal] = useState(false);
  const [showManageBasesModal, setShowManageBasesModal] = useState(false);
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);

  const [configFilters, setConfigFilters] = useState({
    currencyEnabled: true,
    exchangeEnabled: true,
    countryEnabled: true,
    strategyEnabled: true,
    currency: 'all',
    exchange: 'all',
    country: 'all',
    strategy: 'all',
  });

  const pageAssets = useMemo(() => {
    return assets;
  }, [assets]);

  const buildFilterParams = () => {
    const params = {};

    if (configFilters.currencyEnabled && configFilters.currency !== 'all') {
      params.currency = configFilters.currency;
    }

    if (configFilters.exchangeEnabled && configFilters.exchange !== 'all') {
      params.exchange = configFilters.exchange;
    }

    if (configFilters.countryEnabled && configFilters.country !== 'all') {
      params.country = configFilters.country;
    }

    if (configFilters.strategyEnabled && configFilters.strategy !== 'all') {
      params.strategy = configFilters.strategy;
    }

    return params;
  };

  const buildQuery = (params) => {
    const parts = [];

    Object.entries(params).forEach(([key, value]) => {
      if (value === '' || value === null || typeof value === 'undefined') return;

      if (Array.isArray(value)) {
        if (value.length === 0) return;

        value.forEach((item) => {
          parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(item)}`);
        });

        return;
      }

      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    });

    return parts.join('&');
  };

  const fetchData = (page = 1, sort = sortBy, order = sortOrder) => {
    if (!enabled) return;

    const filterParams = buildFilterParams();

    const query = buildQuery({
      ...filterParams,
      search: debouncedSearch,
      page,
      sort,
      order,
      tab: TAB,
    });

    fetch(`/app/analyst/assets?${query}`)
      .then((res) => res.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const data = doc.querySelector('#app-root')?.dataset?.props;

        if (!data) return;

        const parsed = camelizeKeys(JSON.parse(data));

        setAssets(parsed.assets || []);
        setBases(parsed.bases || []);
        setPagination(parsed.pagination || emptyPagination);
        setFilterOptions(parsed.filterOptions || {});
      });
  };

  useEffect(() => {
    if (!enabled) return;

    fetchData(1, sortBy, sortOrder);
  }, [enabled, configFilters, sortBy, sortOrder, debouncedSearch]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (!enabled) return;

    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  }, [enabled, debouncedSearch, configFilters, sortBy, sortOrder]);

  const handlePageChange = (newPage) => {
    fetchData(newPage);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(field);
    setSortOrder('asc');
  };

  const handleChangeFilter = (e) => {
    const { name, value } = e.target;

    setConfigFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditAsset = (assetId) => {
    setEditAssetId(assetId);
    setShowEditAssetModal(true);
  };

  const handleEditModalSuccess = () => {
    setShowEditAssetModal(false);
    fetchData(pagination.currentPage);
  };

  const handleManageBasesClose = () => {
    setShowManageBasesModal(false);
    fetchData(pagination.currentPage);
  };

  const handleManageBasesSuccess = () => {
    setShowManageBasesModal(false);
    fetchData(pagination.currentPage);
  };

  const handleAddAssetClose = () => {
    setShowAddAssetModal(false);
    fetchData(pagination.currentPage);
  };

  const handleAddAssetSuccess = () => {
    setShowAddAssetModal(false);
    fetchData(pagination.currentPage);
  };

  return {
    tab: TAB,

    assets,
    bases,
    filterOptions,
    pagination,
    pageAssets,

    sortBy,
    sortOrder,
    sortByOptions,

    configFilters,
    setConfigFilters,
    handleChangeFilter,

    fetchData,
    handlePageChange,
    handleSort,

    editAssetId,
    showEditAssetModal,
    setShowEditAssetModal,
    handleEditAsset,
    handleEditModalSuccess,

    showManageBasesModal,
    setShowManageBasesModal,
    handleManageBasesClose,
    handleManageBasesSuccess,

    showAddAssetModal,
    setShowAddAssetModal,
    handleAddAssetClose,
    handleAddAssetSuccess,
  };
};

export default useOffshoreSymbols;
